import type { ComponentDefinition } from "../types";

export const DISTRIBUTION: ComponentDefinition[] = [
  {
    // Blue Sea 5032 sales sheet 980018870 Rev.004 (2025), application diagram:
    // two isolated six-slot positive buses, one common negative bus.
    // https://d2pyqm2yd3fw2i.cloudfront.net/files/resources/sales_sheets/BSS-081-SB-1124-Sell_Sheet_ST-Blade_Split.pdf
    id: "dist-bs-5032",
    name: "Blue Sea 5032 ST Blade Split Bus Fuse Block",
    shortName: "Split bus · 5032",
    subtitle: "6 switched + 6 always-on circuits",
    category: "distribution",
    manufacturer: "Blue Sea Systems",
    model: "5032",
    isFuseBlock: true,
    equipmentLimitExemption: "fuse",
    fuseSlotType: "ATC",
    compatibleVoltages: [12, 24],
    width: 210,
    height: 190,
    image: "dist-bs-5032.png",
    fuseBusGroups: [
      { input: "always-in", outputs: ["a1", "a2", "a3", "a4", "a5", "a6"] },
      { input: "switched-in", outputs: ["s1", "s2", "s3", "s4", "s5", "s6"] },
    ],
    interchangeableTerminalGroups: [["a1", "a2", "a3", "a4", "a5", "a6"], ["s1", "s2", "s3", "s4", "s5", "s6"], ["n1", "n2", "n3", "n4", "n5", "n6", "n7", "n8", "n9", "n10", "n11", "n12"]],
    terminals: [
      { id: "always-in", label: "24H +", polarity: "positive", direction: "input", side: "left", offset: .25, maxAmp: 80 },
      { id: "switched-in", label: "SW +", polarity: "positive", direction: "input", side: "left", offset: .75, maxAmp: 80 },
      { id: "in-neg", label: "NEG", polarity: "negative", direction: "bidi", side: "bottom", offset: .1, maxAmp: 100 },
      ...Array.from({ length: 6 }, (_, i) => ({ id: `a${i + 1}`, label: `24H ${i + 1}`, polarity: "positive" as const, direction: "output" as const, side: "right" as const, offset: .04 + i * .075, maxAmp: 30 })),
      ...Array.from({ length: 6 }, (_, i) => ({ id: `s${i + 1}`, label: `SW ${i + 1}`, polarity: "positive" as const, direction: "output" as const, side: "right" as const, offset: .585 + i * .075, maxAmp: 30 })),
      ...Array.from({ length: 12 }, (_, i) => ({ id: `n${i + 1}`, label: `− ${i + 1}`, polarity: "negative" as const, direction: "bidi" as const, side: "bottom" as const, offset: .2 + i * .068, maxAmp: 30 })),
    ],
  },
  {
    id: "junction",
    name: "Junction",
    shortName: "Junction",
    subtitle: "Wire splice",
    category: "distribution",
    renderVariant: "junction",
    width: 14,
    height: 14,
    terminals: [
      {
        id: "j",
        label: "",
        polarity: "neutral",
        direction: "input",
        side: "right",
        offset: 0.5,
        maxAmp: 500,
      },
    ],
  },
  {
    id: "dist-main-switch",
    name: "Main Battery Switch",
    shortName: "Main Switch",
    subtitle: "Battery disconnect 300A",
    category: "distribution",
    renderVariant: "switch",
    width: 60,
    height: 30,
    terminals: [
      {
        id: "a",
        label: "A",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 300,
      },
      {
        id: "b",
        label: "B",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 300,
      },
    ],
  },
  {
    id: "dist-solar-dp-isolator",
    name: "Solar DP Isolator",
    shortName: "DP Isolator",
    subtitle: "Double-pole solar isolator 32A",
    category: "distribution",
    renderVariant: "switch",
    width: 70,
    height: 60,
    terminals: [
      {
        id: "in-pos",
        label: "IN +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 32,
      },
      {
        id: "in-neg",
        label: "IN −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 32,
      },
      {
        id: "out-pos",
        label: "OUT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 32,
      },
      {
        id: "out-neg",
        label: "OUT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 32,
      },
    ],
  },
  {
    id: "dist-galvanic-isolator-promariner-fs30",
    name: "ProMariner ProSafe FS30",
    shortName: "ProSafe FS30",
    subtitle: "Galvanic isolator 30A",
    category: "distribution",
    manufacturer: "ProMariner",
    model: "ProSafe FS30",
    width: 150,
    height: 70,
    terminals: [
      {
        id: "shore-gnd",
        label: "SHORE ⏚",
        polarity: "ground",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 30,
      },
      {
        id: "boat-gnd",
        label: "BOAT ⏚",
        polarity: "ground",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 30,
      },
    ],
    image: "dist-galvanic-isolator-promariner-fs30.webp",
  },
  {
    id: "dist-galvanic-isolator-victron-vdi32",
    name: "Victron Galvanic Isolator VDI-32",
    shortName: "VDI-32",
    subtitle: "Galvanic isolator 32A",
    category: "distribution",
    manufacturer: "Victron",
    model: "VDI-32",
    width: 150,
    height: 70,
    terminals: [
      {
        id: "shore-gnd",
        label: "SHORE ⏚",
        polarity: "ground",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 32,
      },
      {
        id: "boat-gnd",
        label: "BOAT ⏚",
        polarity: "ground",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 32,
      },
    ],
    image: "dist-galvanic-isolator-victron-vdi32.webp",
  },
  {
    id: "dist-bs-miniswitch-5992",
    name: "Blue Sea MiniSwitch 5992",
    shortName: "MiniSwitch 5992",
    subtitle: "Terminal-mount disconnect 150A",
    category: "distribution",
    manufacturer: "Blue Sea Systems",
    model: "5992",
    compatibleVoltages: [12, 24],
    renderVariant: "switch",
    width: 60,
    height: 30,
    // Blue Sea 5992 specifications / 88380 sales sheet: 150 A continuous,
    // 225 A for 5 min, 450 A starting (duration unpublished), 32 V maximum.
    // These are contact ratings, not load draw or regulated output.
    terminals: [
      {
        id: "a",
        label: "BATTERY",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 150,
      },
      {
        id: "b",
        label: "CABLE",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 150,
      },
    ],
    image: "dist-bs-miniswitch-5992.webp",
  },
  {
    id: "dist-bs-mseries-6005",
    name: "Blue Sea m-Series 6005",
    shortName: "BS m-Series",
    subtitle: "Battery switch 300A",
    category: "distribution",
    manufacturer: "Blue Sea Systems",
    model: "6005",
    renderVariant: "switch",
    width: 60,
    height: 30,
    terminals: [
      {
        id: "a",
        label: "A",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 300,
      },
      {
        id: "b",
        label: "B",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 300,
      },
    ],
    image: "dist-bs-mseries-6005.webp",
  },
  {
    id: "dist-spst-switch",
    name: "SPST Switch",
    shortName: "SPST",
    subtitle: "Single-pole on/off switch 15A",
    category: "distribution",
    renderVariant: "switch",
    width: 60,
    height: 30,
    terminals: [
      {
        id: "a",
        label: "A",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 15,
      },
      {
        id: "b",
        label: "B",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 15,
      },
    ],
  },
  {
    id: "dist-bs-eseries-9001e",
    name: "Blue Sea e-Series Selector 9002e",
    shortName: "BS e-Series",
    subtitle: "1-2-Both selector 350A",
    category: "distribution",
    manufacturer: "Blue Sea Systems",
    model: "9002e",
    width: 190,
    height: 110,
    terminals: [
      {
        id: "1",
        label: "1",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 350,
      },
      {
        id: "2",
        label: "2",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 350,
      },
      {
        id: "c",
        label: "C",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 350,
      },
    ],
    simulateStates: {
      default: "both",
      options: [
        {
          id: "1",
          label: "1",
          intraLinks: [["1", "c"]],
        },
        {
          id: "2",
          label: "2",
          intraLinks: [["2", "c"]],
        },
        {
          id: "both",
          label: "Both",
          intraLinks: [
            ["1", "c"],
            ["2", "c"],
          ],
        },
        {
          id: "off",
          label: "Off",
          ampOverride: 0,
        },
      ],
    },
    image: "dist-bs-eseries-9001e.webp",
  },
  {
    id: "dist-1-2-both",
    name: "1-2-Both Selector",
    shortName: "1-2-Both",
    subtitle: "Battery selector switch",
    category: "distribution",
    width: 190,
    height: 110,
    terminals: [
      {
        id: "1",
        label: "1",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 300,
      },
      {
        id: "2",
        label: "2",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 300,
      },
      {
        id: "c",
        label: "C",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 300,
      },
    ],
    simulateStates: {
      default: "both",
      options: [
        {
          id: "1",
          label: "1",
          intraLinks: [["1", "c"]],
        },
        {
          id: "2",
          label: "2",
          intraLinks: [["2", "c"]],
        },
        {
          id: "both",
          label: "Both",
          intraLinks: [
            ["1", "c"],
            ["2", "c"],
          ],
        },
        {
          id: "off",
          label: "Off",
          ampOverride: 0,
        },
      ],
    },
  },
  {
    id: "dist-bs-6011",
    name: "Blue Sea 6011 Dual Circuit Plus",
    shortName: "BS 6011",
    subtitle: "Dual-circuit selector",
    category: "distribution",
    manufacturer: "Blue Sea Systems",
    model: "6011",
    width: 200,
    height: 115,
    terminals: [
      {
        id: "1",
        label: "1",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 300,
      },
      {
        id: "2",
        label: "2",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 300,
      },
      {
        id: "c1",
        label: "C1",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 300,
      },
      {
        id: "c2",
        label: "C2",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 300,
      },
    ],
    simulateStates: {
      default: "on",
      options: [
        {
          id: "on",
          label: "On",
          intraLinks: [
            ["1", "c1"],
            ["2", "c2"],
          ],
        },
        {
          id: "combine",
          label: "Combine",
          intraLinks: [
            ["1", "c1"],
            ["1", "c2"],
            ["2", "c1"],
            ["2", "c2"],
          ],
        },
        {
          id: "off",
          label: "Off",
          ampOverride: 0,
        },
      ],
    },
    image: "dist-bs-6011.webp",
  },
  {
    id: "dist-bs-7610-si-acr",
    name: "Blue Sea SI-ACR 7610",
    shortName: "SI-ACR 7610",
    subtitle: "Automatic charging relay 120A",
    category: "distribution",
    manufacturer: "Blue Sea Systems",
    model: "7610",
    // Blue Sea 990170140 rev 011, pp1–2:
    // https://d2pyqm2yd3fw2i.cloudfront.net/files/resources/instructions/990170140.pdf
    compatibleVoltages: [12, 24],
    width: 210,
    height: 120,
    terminals: [
      { id: "a", label: "A", polarity: "positive", direction: "bidi", side: "left", offset: 0.3, maxAmp: 120 },
      { id: "b", label: "B", polarity: "positive", direction: "bidi", side: "right", offset: 0.3, maxAmp: 120 },
      // Protected control-return allowance; operating draw is 175 mA combined,
      // 15 mA open. This is not a negative bus or a load-return connection.
      { id: "gnd", label: "GND (1A fused)", polarity: "negative", direction: "input", side: "bottom", offset: 0.5, maxAmp: 1 },
    ],
    simulateStates: {
      default: "isolated",
      options: [
        { id: "combined", label: "Combined", intraLinks: [["a", "b"]] },
        { id: "isolated", label: "Isolated", ampOverride: 0 },
      ],
    },
    image: "dist-bs-7610-si-acr.webp",
  },
  {
    id: "dist-bs-7622-ml-acr",
    name: "Blue Sea ML-ACR 7622",
    shortName: "ML-ACR",
    subtitle: "Auto charging relay 500A",
    category: "distribution",
    manufacturer: "Blue Sea Systems",
    model: "7622",
    width: 200,
    height: 115,
    terminals: [
      {
        id: "a",
        label: "A",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 500,
      },
      {
        id: "b",
        label: "B",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 500,
      },
    ],
    // Manual contact scenarios; automatic voltage/control behavior is not
    // inferred from nominal battery voltage. Static design retains both banks.
    simulateStates: {
      default: "isolated",
      options: [
        { id: "combined", label: "Combined (manual)", intraLinks: [["a", "b"]] },
        { id: "isolated", label: "Isolated (manual)", ampOverride: 0 },
      ],
    },
    image: "dist-bs-7622-ml-acr.webp",
  },
  {
    id: "dist-bs-7700-ml-rbs",
    name: "Blue Sea ML-RBS 7700",
    shortName: "ML-RBS",
    subtitle: "Remote battery switch 500A",
    category: "distribution",
    manufacturer: "Blue Sea Systems",
    model: "7700",
    width: 220,
    height: 130,
    terminals: [
      {
        id: "a",
        label: "A",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 500,
      },
      {
        id: "b",
        label: "B",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 500,
      },
    ],
    image: "dist-bs-7700-ml-rbs.webp",
  },
  {
    id: "dist-pos-bus",
    name: "Positive Bus Bar",
    shortName: "+ Bus",
    subtitle: "Positive bus bar 150A",
    category: "distribution",
    renderVariant: "busbar",
    equipmentLimitExemption: "busbar",
    width: 44,
    height: 140,
    terminals: [
      {
        id: "a",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 150,
      },
      {
        id: "b",
        label: "1",
        polarity: "positive",
        direction: "bidi",
        side: "right",
        offset: 0.1,
        maxAmp: 150,
      },
      {
        id: "c",
        label: "2",
        polarity: "positive",
        direction: "bidi",
        side: "right",
        offset: 0.3,
        maxAmp: 150,
      },
      {
        id: "d",
        label: "3",
        polarity: "positive",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 150,
      },
      {
        id: "e",
        label: "4",
        polarity: "positive",
        direction: "bidi",
        side: "right",
        offset: 0.7,
        maxAmp: 150,
      },
      {
        id: "f",
        label: "5",
        polarity: "positive",
        direction: "bidi",
        side: "right",
        offset: 0.9,
        maxAmp: 150,
      },
    ],
  },
  {
    id: "dist-neg-bus",
    name: "Negative Bus Bar",
    shortName: "− Bus",
    subtitle: "Negative bus bar 150A",
    category: "distribution",
    renderVariant: "busbar",
    equipmentLimitExemption: "busbar",
    width: 44,
    height: 140,
    terminals: [
      {
        id: "a",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 150,
      },
      {
        id: "b",
        label: "1",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.1,
        maxAmp: 150,
      },
      {
        id: "c",
        label: "2",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.3,
        maxAmp: 150,
      },
      {
        id: "d",
        label: "3",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 150,
      },
      {
        id: "e",
        label: "4",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.7,
        maxAmp: 150,
      },
      {
        id: "f",
        label: "5",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.9,
        maxAmp: 150,
      },
    ],
  },
  {
    id: "dist-chassis-ground",
    name: "Chassis Ground",
    shortName: "Chassis",
    subtitle: "Chassis bonding point 150A",
    category: "distribution",
    renderVariant: "busbar",
    equipmentLimitExemption: "busbar",
    width: 44,
    height: 140,
    terminals: [
      {
        id: "a",
        label: "−",
        polarity: "ground",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 150,
      },
      {
        id: "b",
        label: "1",
        polarity: "ground",
        direction: "bidi",
        side: "right",
        offset: 0.1,
        maxAmp: 150,
      },
      {
        id: "c",
        label: "2",
        polarity: "ground",
        direction: "bidi",
        side: "right",
        offset: 0.3,
        maxAmp: 150,
      },
      {
        id: "d",
        label: "3",
        polarity: "ground",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 150,
      },
      {
        id: "e",
        label: "4",
        polarity: "ground",
        direction: "bidi",
        side: "right",
        offset: 0.7,
        maxAmp: 150,
      },
      {
        id: "f",
        label: "5",
        polarity: "ground",
        direction: "bidi",
        side: "right",
        offset: 0.9,
        maxAmp: 150,
      },
    ],
  },
  {
    id: "chassis-ground-symbol",
    name: "Ground symbol",
    shortName: "GND",
    subtitle: "Implicit chassis-ground point",
    category: "distribution",
    renderVariant: "chassis-ground",
    width: 28,
    height: 28,
    terminals: [
      {
        id: "g",
        label: "",
        polarity: "ground",
        direction: "bidi",
        side: "top",
        offset: 0.5,
        maxAmp: 500,
      },
    ],
  },
  {
    id: "dist-bep-pro-installer-lb-1",
    name: "BEP Pro Installer LB-1 Link Bar",
    shortName: "LB-1 650A",
    subtitle: "Pro Installer link bar 650A",
    category: "distribution",
    manufacturer: "BEP",
    model: "779-LB-1-B",
    // BEP Catalog, Pro Installer Link Bars specifications (viewer page 39):
    // https://docs.navico.com/view/777950233/39/
    equipmentLimitExemption: "busbar",
    width: 120,
    height: 60,
    terminals: [
      {
        id: "a",
        label: "1",
        polarity: "neutral",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 650,
      },
      {
        id: "b",
        label: "2",
        polarity: "neutral",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 650,
      },
    ],
  },
  {
    id: "dist-bs-powerbar-2114",
    name: "Blue Sea Common 150A BusBar",
    shortName: "BS 2303",
    subtitle: "Common bus bar 150A",
    category: "distribution",
    manufacturer: "Blue Sea Systems",
    model: "2303",
    renderVariant: "busbar",
    equipmentLimitExemption: "busbar",
    width: 44,
    height: 140,
    terminals: [
      {
        id: "a",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 150,
      },
      {
        id: "b",
        label: "1",
        polarity: "positive",
        direction: "bidi",
        side: "right",
        offset: 0.2,
        maxAmp: 150,
      },
      {
        id: "c",
        label: "2",
        polarity: "positive",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 150,
      },
      {
        id: "d",
        label: "3",
        polarity: "positive",
        direction: "bidi",
        side: "right",
        offset: 0.8,
        maxAmp: 150,
      },
    ],
  },
  {
    id: "dist-bs-powerbar-2104",
    name: "Blue Sea PowerBar 600A 2104",
    shortName: "PowerBar 2104",
    subtitle: "Four-stud common bus bar 600A",
    category: "distribution",
    manufacturer: "Blue Sea Systems",
    model: "2104",
    // Blue Sea 2104 specifications + customer drawing Rev 2, sheet 1.
    // One common conductor, usable on either DC rail; 600 A is not load demand.
    // Only the four 3/8-inch studs are modeled, not the small #8-32 screws.
    renderVariant: "busbar",
    equipmentLimitExemption: "busbar",
    width: 44,
    height: 140,
    terminals: [
      { id: "a", label: "1", polarity: "neutral", direction: "bidi", side: "left", offset: 0.5, maxAmp: 600 },
      { id: "b", label: "2", polarity: "neutral", direction: "bidi", side: "right", offset: 0.2, maxAmp: 600 },
      { id: "c", label: "3", polarity: "neutral", direction: "bidi", side: "right", offset: 0.5, maxAmp: 600 },
      { id: "d", label: "4", polarity: "neutral", direction: "bidi", side: "right", offset: 0.8, maxAmp: 600 },
    ],
  },
  {
    id: "dist-bs-2126-common",
    name: "Blue Sea MaxiBus 250A 2126",
    shortName: "MaxiBus 250",
    subtitle: "Common bus bar 250A",
    category: "distribution",
    manufacturer: "Blue Sea Systems",
    model: "2126",
    renderVariant: "busbar",
    equipmentLimitExemption: "busbar",
    width: 44,
    height: 110,
    terminals: [
      {
        id: "a",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "left",
        offset: 0.5,
        maxAmp: 250,
      },
      {
        id: "e",
        label: "4",
        polarity: "negative",
        direction: "bidi",
        side: "left",
        offset: 0.2,
        maxAmp: 250,
      },
      {
        id: "f",
        label: "5",
        polarity: "negative",
        direction: "bidi",
        side: "left",
        offset: 0.8,
        maxAmp: 250,
      },
      {
        id: "b",
        label: "1",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.2,
        maxAmp: 250,
      },
      {
        id: "c",
        label: "2",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.5,
        maxAmp: 250,
      },
      {
        id: "d",
        label: "3",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.8,
        maxAmp: 250,
      },
    ],
    image: "dist-bs-2126-common.webp",
  },
  {
    id: "dist-terminal-block",
    name: "Terminal Block 4-way",
    shortName: "Term Block",
    subtitle: "4-way terminal block",
    category: "distribution",
    width: 160,
    height: 130,
    terminals: [
      {
        id: "l0",
        label: "1",
        polarity: "signal",
        direction: "bidi",
        side: "left",
        offset: 0.15,
        maxAmp: 30,
      },
      {
        id: "l1",
        label: "2",
        polarity: "signal",
        direction: "bidi",
        side: "left",
        offset: 0.375,
        maxAmp: 30,
      },
      {
        id: "l2",
        label: "3",
        polarity: "signal",
        direction: "bidi",
        side: "left",
        offset: 0.6,
        maxAmp: 30,
      },
      {
        id: "l3",
        label: "4",
        polarity: "signal",
        direction: "bidi",
        side: "left",
        offset: 0.825,
        maxAmp: 30,
      },
      {
        id: "r0",
        label: "1",
        polarity: "signal",
        direction: "bidi",
        side: "right",
        offset: 0.15,
        maxAmp: 30,
      },
      {
        id: "r1",
        label: "2",
        polarity: "signal",
        direction: "bidi",
        side: "right",
        offset: 0.375,
        maxAmp: 30,
      },
      {
        id: "r2",
        label: "3",
        polarity: "signal",
        direction: "bidi",
        side: "right",
        offset: 0.6,
        maxAmp: 30,
      },
      {
        id: "r3",
        label: "4",
        polarity: "signal",
        direction: "bidi",
        side: "right",
        offset: 0.825,
        maxAmp: 30,
      },
    ],
  },
  {
    id: "dist-victron-bp-100",
    name: "Victron BatteryProtect 100",
    shortName: "BP-100",
    subtitle: "Low-voltage disconnect 100A",
    category: "distribution",
    manufacturer: "Victron",
    model: "BatteryProtect 100",
    width: 190,
    height: 100,
    terminals: [
      {
        id: "in",
        label: "IN",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 100,
      },
      {
        id: "out",
        label: "OUT",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 100,
      },
      {
        // BatteryProtect manual §3.1: dedicated 1.5 mm² GND reference lead,
        // protected at 300 mA. It must not serve another equipment return.
        // Signal polarity keeps this reference out of the load-current bus.
        // https://www.victronenergy.com/media/pg/BatteryProtect_12V_24V/en/installation-and-wiring-examples.html
        id: "gnd",
        label: "GND",
        polarity: "signal",
        color: "#475569",
        direction: "input",
        side: "bottom",
        offset: 0.5,
        maxAmp: 0,
      },
    ],
    image: "dist-victron-bp-100.webp",
  },
  {
    id: "dist-victron-bp-220",
    name: "Victron BatteryProtect 220",
    shortName: "BP-220",
    subtitle: "Low-voltage disconnect 220A",
    category: "distribution",
    manufacturer: "Victron",
    model: "BatteryProtect 220",
    width: 200,
    height: 105,
    terminals: [
      {
        id: "in",
        label: "IN",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 220,
      },
      {
        id: "out",
        label: "OUT",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 220,
      },
      {
        // Same manufacturer GND reference circuit as the 100 A model above.
        id: "gnd",
        label: "GND",
        polarity: "signal",
        color: "#475569",
        direction: "input",
        side: "bottom",
        offset: 0.5,
        maxAmp: 0,
      },
    ],
    image: "dist-victron-bp-220.webp",
  },
  {
    id: "dist-victron-cyrix-li-230",
    name: "Victron Cyrix-Li-Charge 230",
    shortName: "Cyrix-Li 230",
    subtitle: "Combiner relay 230A",
    category: "distribution",
    manufacturer: "Victron",
    model: "Cyrix-Li-Charge 230",
    width: 200,
    height: 110,
    terminals: [
      {
        id: "a",
        label: "A",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 230,
      },
      {
        id: "b",
        label: "B",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 230,
      },
    ],
    image: "dist-victron-cyrix-li-230.webp",
  },
  {
    id: "dist-victron-lynx-distributor",
    name: "Victron Lynx Distributor",
    shortName: "Lynx Dist",
    subtitle: "Modular fuse bus 1000A",
    category: "distribution",
    manufacturer: "Victron",
    model: "Lynx Distributor M10",
    isFuseBlock: true,
    equipmentLimitExemption: "fuse",
    fuseSlotType: "MEGA",
    interchangeableTerminalGroups: [["c1", "c2", "c3", "c4"], ["n1", "n2", "n3", "n4"]],
    width: 260,
    height: 140,
    // Victron manual rev 04, §5.2/§10: 1000 A module, MEGA fuse compatibility;
    // no separate thermal rating is published for the branch studs.
    // https://www.victronenergy.com/upload/documents/Lynx_Distributor/24531-Lynx_Distributor_Manual-pdf-en.pdf
    terminals: [
      {
        id: "in-pos",
        label: "IN +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 1000,
      },
      {
        id: "in-neg",
        label: "IN −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 1000,
      },
      {
        id: "c1",
        label: "1",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.15,
        maxAmp: 0,
      },
      {
        id: "c2",
        label: "2",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.4,
        maxAmp: 0,
      },
      {
        id: "c3",
        label: "3",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.65,
        maxAmp: 0,
      },
      {
        id: "c4",
        label: "4",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.9,
        maxAmp: 0,
      },
      {
        id: "n1",
        label: "1 −",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.15,
        maxAmp: 0,
      },
      {
        id: "n2",
        label: "2 −",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.4,
        maxAmp: 0,
      },
      {
        id: "n3",
        label: "3 −",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.65,
        maxAmp: 0,
      },
      {
        id: "n4",
        label: "4 −",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.9,
        maxAmp: 0,
      },
    ],
    image: "dist-victron-lynx-distributor.webp",
  },
  {
    id: "dist-victron-lynx-power-in",
    name: "Victron Lynx Power In",
    shortName: "Lynx Pwr In",
    subtitle: "Modular DC bus 1000A",
    category: "distribution",
    manufacturer: "Victron",
    model: "Lynx Power In",
    equipmentLimitExemption: "busbar",
    interchangeableTerminalGroups: [
      ["p1", "p2"],
      ["n1", "n2"],
      ["p3", "p4"],
      ["n3", "n4"],
    ],
    width: 260,
    height: 160,
    // Victron publishes a 1000 A module rating, not a per-stud limit.
    // https://www.victronenergy.com/media/pg/Lynx_Power_In/en/technical-specifications-lynx-power-in.html
    // maxAmp: 0 means unpublished; do not divide bus capacity by four.
    terminals: [
      {
        id: "p1",
        label: "1+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.2,
        maxAmp: 0,
      },
      {
        id: "p2",
        label: "2+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.4,
        maxAmp: 0,
      },
      {
        id: "n1",
        label: "1−",
        polarity: "negative",
        direction: "bidi",
        side: "left",
        offset: 0.6,
        maxAmp: 0,
      },
      {
        id: "n2",
        label: "2−",
        polarity: "negative",
        direction: "bidi",
        side: "left",
        offset: 0.8,
        maxAmp: 0,
      },
      {
        id: "p3",
        label: "3+",
        polarity: "positive",
        direction: "bidi",
        side: "right",
        offset: 0.2,
        maxAmp: 0,
      },
      {
        id: "p4",
        label: "4+",
        polarity: "positive",
        direction: "bidi",
        side: "right",
        offset: 0.4,
        maxAmp: 0,
      },
      {
        id: "n3",
        label: "3−",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.6,
        maxAmp: 0,
      },
      {
        id: "n4",
        label: "4−",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.8,
        maxAmp: 0,
      },
    ],
    image: "dist-victron-lynx-power-in.webp",
  },
  {
    id: "dist-victron-lynx-classt-power-in",
    name: "Victron Lynx Class-T Power In",
    shortName: "Lynx Class-T",
    subtitle: "Class-T fused DC bus 800A",
    category: "distribution",
    manufacturer: "Victron",
    model: "Lynx Class-T Power In (M10)",
    isFuseBlock: true,
    equipmentLimitExemption: "fuse",
    fuseSlotType: "ClassT",
    interchangeableTerminalGroups: [["bat1", "bat2"]],
    width: 260,
    height: 160,
    terminals: [
      {
        id: "bat1",
        label: "BATT 1 +",
        polarity: "positive",
        direction: "output",
        side: "left",
        offset: 0.2,
        maxAmp: 400,
      },
      {
        id: "bat2",
        label: "BATT 2 +",
        polarity: "positive",
        direction: "output",
        side: "left",
        offset: 0.4,
        maxAmp: 400,
      },
      {
        id: "bat1-neg",
        label: "BATT 1 −",
        polarity: "negative",
        direction: "bidi",
        side: "left",
        offset: 0.6,
        maxAmp: 400,
      },
      {
        id: "bat2-neg",
        label: "BATT 2 −",
        polarity: "negative",
        direction: "bidi",
        side: "left",
        offset: 0.8,
        maxAmp: 400,
      },
      {
        id: "in-pos",
        label: "BUS +",
        polarity: "positive",
        direction: "input",
        side: "right",
        offset: 0.3,
        maxAmp: 800,
      },
      {
        id: "in-neg",
        label: "BUS −",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.7,
        maxAmp: 800,
      },
      {
        id: "gnd",
        label: "⏚",
        polarity: "ground",
        direction: "bidi",
        side: "bottom",
        offset: 0.5,
        maxAmp: 400,
      },
    ],
    image: "dist-victron-lynx-classt-power-in.webp",
  },
  {
    id: "dist-anderson-sb50",
    name: "Anderson SB50 Connector",
    shortName: "SB50",
    subtitle: "Power connector 50A",
    category: "distribution",
    manufacturer: "Anderson Power Products",
    model: "SB50",
    width: 130,
    height: 70,
    terminals: [
      {
        id: "a",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.35,
        maxAmp: 50,
      },
      {
        id: "b",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "left",
        offset: 0.7,
        maxAmp: 50,
      },
      {
        id: "c",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "right",
        offset: 0.35,
        maxAmp: 50,
      },
      {
        id: "d",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.7,
        maxAmp: 50,
      },
    ],
    image: "dist-anderson-sb50.webp",
  },
  {
    id: "dist-anderson-sb175",
    name: "Anderson SB175 Connector",
    shortName: "SB175",
    subtitle: "Power connector 175A",
    category: "distribution",
    manufacturer: "Anderson Power Products",
    model: "SB175",
    width: 150,
    height: 80,
    terminals: [
      {
        id: "a",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "left",
        offset: 0.35,
        maxAmp: 175,
      },
      {
        id: "b",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "left",
        offset: 0.7,
        maxAmp: 175,
      },
      {
        id: "c",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "right",
        offset: 0.35,
        maxAmp: 175,
      },
      {
        id: "d",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "right",
        offset: 0.7,
        maxAmp: 175,
      },
    ],
    image: "dist-anderson-sb175.webp",
  },
  {
    id: "dist-holder-mega",
    name: "MEGA fuse holder",
    shortName: "MEGA holder",
    subtitle: "Inline MEGA bolt-on holder",
    category: "distribution",
    isFuseBlock: true,
    equipmentLimitExemption: "fuse",
    fuseSlotType: "MEGA",
    renderVariant: "fuse-holder",
    width: 60,
    height: 40,
    terminals: [
      {
        id: "in",
        label: "in",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 500,
      },
      {
        id: "out",
        label: "out",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 500,
      },
    ],
  },
  {
    id: "dist-holder-agc",
    name: "Blue Sea 5060 AGC/MDL inline glass fuse holder",
    shortName: "Glass fuse holder",
    subtitle: "1 A MDL slow-blow fuse",
    category: "distribution",
    manufacturer: "Blue Sea Systems",
    model: "5060",
    renderVariant: "fuse-holder",
    isFuseBlock: true,
    equipmentLimitExemption: "fuse",
    fuseSlotType: "MDL",
    defaultFuseAmp: 1,
    width: 80,
    height: 40,
    terminals: [
      { id: "in", label: "IN", polarity: "positive", direction: "input", side: "left", offset: 0.5, maxAmp: 30 },
      { id: "out", label: "OUT", polarity: "positive", direction: "output", side: "right", offset: 0.5, maxAmp: 30 },
    ],
  },
  {
    id: "dist-holder-anl",
    name: "ANL fuse holder",
    shortName: "ANL holder",
    subtitle: "Inline ANL holder",
    category: "distribution",
    isFuseBlock: true,
    equipmentLimitExemption: "fuse",
    fuseSlotType: "ANL",
    renderVariant: "fuse-holder",
    width: 60,
    height: 40,
    terminals: [
      {
        id: "in",
        label: "in",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 750,
      },
      {
        id: "out",
        label: "out",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 750,
      },
    ],
  },
  {
    id: "dist-holder-classt",
    name: "Class T fuse holder",
    shortName: "Class T holder",
    subtitle: "Inline Class T holder",
    category: "distribution",
    isFuseBlock: true,
    equipmentLimitExemption: "fuse",
    fuseSlotType: "ClassT",
    renderVariant: "fuse-holder",
    width: 60,
    height: 40,
    terminals: [
      {
        id: "in",
        label: "in",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 400,
      },
      {
        id: "out",
        label: "out",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 400,
      },
    ],
  },
  {
    id: "dist-holder-mrbf",
    name: "MRBF terminal fuse holder",
    shortName: "MRBF holder",
    subtitle: "Battery-terminal MRBF holder",
    category: "distribution",
    manufacturer: "Blue Sea Systems",
    model: "5191",
    image: "dist-holder-mrbf.webp",
    isFuseBlock: true,
    equipmentLimitExemption: "fuse",
    fuseSlotType: "MRBF",
    renderVariant: "fuse-holder",
    width: 60,
    height: 40,
    terminals: [
      {
        id: "in",
        label: "in",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 300,
      },
      {
        id: "out",
        label: "out",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 300,
      },
    ],
  },
  {
    id: "dist-relay-200",
    name: "Continuous-Duty Relay 200A",
    shortName: "Relay 200A",
    subtitle: "Solenoid / contactor 200A",
    category: "distribution",
    renderVariant: "switch",
    width: 110,
    height: 70,
    terminals: [
      {
        id: "a",
        label: "A",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 200,
      },
      {
        id: "b",
        label: "B",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.4,
        maxAmp: 200,
      },
      {
        id: "coil-pos",
        label: "S+",
        polarity: "signal",
        direction: "input",
        side: "bottom",
        offset: 0.35,
        maxAmp: 2,
      },
      {
        id: "coil-neg",
        label: "S−",
        polarity: "signal",
        direction: "input",
        side: "bottom",
        offset: 0.65,
        maxAmp: 2,
      },
    ],
    simulateStates: {
      default: "off",
      options: [
        {
          id: "on",
          label: "Closed",
          ampMultiplier: 1,
        },
        {
          id: "off",
          label: "Open",
          ampOverride: 0,
        },
      ],
    },
  },
  {
    id: "dist-branch-panel-6",
    name: "6-Circuit Switch Panel",
    shortName: "Switch Panel 6",
    subtitle: "Fused branch panel, 6 circuits",
    category: "distribution",
    isFuseBlock: true,
    equipmentLimitExemption: "fuse",
    fuseSlotType: "ATC",
    interchangeableTerminalGroups: [["c1", "c2", "c3", "c4", "c5", "c6"]],
    width: 200,
    height: 180,
    terminals: [
      {
        id: "in-pos",
        label: "IN +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 100,
      },
      {
        id: "c1",
        label: "1",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.1,
        maxAmp: 30,
      },
      {
        id: "c2",
        label: "2",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.25,
        maxAmp: 30,
      },
      {
        id: "c3",
        label: "3",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.4,
        maxAmp: 30,
      },
      {
        id: "c4",
        label: "4",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.55,
        maxAmp: 30,
      },
      {
        id: "c5",
        label: "5",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 30,
      },
      {
        id: "c6",
        label: "6",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.85,
        maxAmp: 30,
      },
    ],
  },
  {
    id: "dist-bs-5025",
    name: "Blue Sea 5025 ST Blade Fuse Block",
    shortName: "BS 5025",
    subtitle: "6-circuit fuse panel + neg bus",
    category: "distribution",
    manufacturer: "Blue Sea Systems",
    model: "5025",
    image: "dist-bs-5025.webp",
    isFuseBlock: true,
    equipmentLimitExemption: "fuse",
    fuseSlotType: "ATC",
    interchangeableTerminalGroups: [
      ["c1", "c2", "c3", "c4", "c5", "c6"],
      ["n1", "n2", "n3", "n4", "n5", "n6"],
    ],
    width: 200,
    height: 180,
    terminals: [
      {
        id: "in-pos",
        label: "IN +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 100,
      },
      {
        id: "in-neg",
        label: "IN −",
        polarity: "negative",
        direction: "bidi",
        side: "left",
        offset: 0.7,
        maxAmp: 100,
      },
      {
        id: "c1",
        label: "1",
        polarity: "positive",
        direction: "output",
        side: "top",
        offset: 0.085,
        maxAmp: 30,
      },
      {
        id: "c2",
        label: "2",
        polarity: "positive",
        direction: "output",
        side: "top",
        offset: 0.245,
        maxAmp: 30,
      },
      {
        id: "c3",
        label: "3",
        polarity: "positive",
        direction: "output",
        side: "top",
        offset: 0.405,
        maxAmp: 30,
      },
      {
        id: "c4",
        label: "4",
        polarity: "positive",
        direction: "output",
        side: "top",
        offset: 0.595,
        maxAmp: 30,
      },
      {
        id: "c5",
        label: "5",
        polarity: "positive",
        direction: "output",
        side: "top",
        offset: 0.755,
        maxAmp: 30,
      },
      {
        id: "c6",
        label: "6",
        polarity: "positive",
        direction: "output",
        side: "top",
        offset: 0.915,
        maxAmp: 30,
      },
      {
        id: "n1",
        label: "1",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.085,
        maxAmp: 30,
      },
      {
        id: "n2",
        label: "2",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.245,
        maxAmp: 30,
      },
      {
        id: "n3",
        label: "3",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.405,
        maxAmp: 30,
      },
      {
        id: "n4",
        label: "4",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.595,
        maxAmp: 30,
      },
      {
        id: "n5",
        label: "5",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.755,
        maxAmp: 30,
      },
      {
        id: "n6",
        label: "6",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.915,
        maxAmp: 30,
      },
    ],
  },
  {
    id: "dist-bs-5026",
    name: "Blue Sea 5026 ST Blade Fuse Block",
    shortName: "BS 5026",
    subtitle: "12-circuit fuse panel + neg bus",
    category: "distribution",
    manufacturer: "Blue Sea Systems",
    model: "5026",
    isFuseBlock: true,
    equipmentLimitExemption: "fuse",
    fuseSlotType: "ATC",
    interchangeableTerminalGroups: [
      [
        "c1",
        "c2",
        "c3",
        "c4",
        "c5",
        "c6",
        "c7",
        "c8",
        "c9",
        "c10",
        "c11",
        "c12",
      ],
      [
        "n1",
        "n2",
        "n3",
        "n4",
        "n5",
        "n6",
        "n7",
        "n8",
        "n9",
        "n10",
        "n11",
        "n12",
      ],
    ],
    width: 320,
    height: 180,
    terminals: [
      {
        id: "in-pos",
        label: "IN +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 100,
      },
      {
        id: "in-neg",
        label: "IN −",
        polarity: "negative",
        direction: "bidi",
        side: "left",
        offset: 0.7,
        maxAmp: 100,
      },
      {
        id: "c1",
        label: "1",
        polarity: "positive",
        direction: "output",
        side: "top",
        offset: 0.045,
        maxAmp: 30,
      },
      {
        id: "c2",
        label: "2",
        polarity: "positive",
        direction: "output",
        side: "top",
        offset: 0.125,
        maxAmp: 30,
      },
      {
        id: "c3",
        label: "3",
        polarity: "positive",
        direction: "output",
        side: "top",
        offset: 0.205,
        maxAmp: 30,
      },
      {
        id: "c4",
        label: "4",
        polarity: "positive",
        direction: "output",
        side: "top",
        offset: 0.285,
        maxAmp: 30,
      },
      {
        id: "c5",
        label: "5",
        polarity: "positive",
        direction: "output",
        side: "top",
        offset: 0.365,
        maxAmp: 30,
      },
      {
        id: "c6",
        label: "6",
        polarity: "positive",
        direction: "output",
        side: "top",
        offset: 0.445,
        maxAmp: 30,
      },
      {
        id: "c7",
        label: "7",
        polarity: "positive",
        direction: "output",
        side: "top",
        offset: 0.555,
        maxAmp: 30,
      },
      {
        id: "c8",
        label: "8",
        polarity: "positive",
        direction: "output",
        side: "top",
        offset: 0.635,
        maxAmp: 30,
      },
      {
        id: "c9",
        label: "9",
        polarity: "positive",
        direction: "output",
        side: "top",
        offset: 0.715,
        maxAmp: 30,
      },
      {
        id: "c10",
        label: "10",
        polarity: "positive",
        direction: "output",
        side: "top",
        offset: 0.795,
        maxAmp: 30,
      },
      {
        id: "c11",
        label: "11",
        polarity: "positive",
        direction: "output",
        side: "top",
        offset: 0.875,
        maxAmp: 30,
      },
      {
        id: "c12",
        label: "12",
        polarity: "positive",
        direction: "output",
        side: "top",
        offset: 0.955,
        maxAmp: 30,
      },
      {
        id: "n1",
        label: "1",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.045,
        maxAmp: 30,
      },
      {
        id: "n2",
        label: "2",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.125,
        maxAmp: 30,
      },
      {
        id: "n3",
        label: "3",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.205,
        maxAmp: 30,
      },
      {
        id: "n4",
        label: "4",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.285,
        maxAmp: 30,
      },
      {
        id: "n5",
        label: "5",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.365,
        maxAmp: 30,
      },
      {
        id: "n6",
        label: "6",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.445,
        maxAmp: 30,
      },
      {
        id: "n7",
        label: "7",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.555,
        maxAmp: 30,
      },
      {
        id: "n8",
        label: "8",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.635,
        maxAmp: 30,
      },
      {
        id: "n9",
        label: "9",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.715,
        maxAmp: 30,
      },
      {
        id: "n10",
        label: "10",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.795,
        maxAmp: 30,
      },
      {
        id: "n11",
        label: "11",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.875,
        maxAmp: 30,
      },
      {
        id: "n12",
        label: "12",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.955,
        maxAmp: 30,
      },
    ],
  },
  {
    id: "dist-philippi-stv108",
    name: "PHILIPPI STV 108 Switch Panel",
    shortName: "STV 108",
    subtitle: "8-circuit switch panel + auto CBs",
    category: "distribution",
    manufacturer: "PHILIPPI",
    model: "STV 108",
    isFuseBlock: true,
    equipmentLimitExemption: "fuse",
    interchangeableTerminalGroups: [
      ["c1", "c2", "c3", "c4", "c5", "c6", "c7", "c8"],
    ],
    width: 160,
    height: 200,
    terminals: [
      {
        id: "in-pos",
        // The manual rates its supplied 6 mm² lead at 29 A, not this contact.
        // Supplied-lead protection belongs to installation-limits.ts.
        maxAmp: 0,
        label: "IN +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.5,
      },
      {
        id: "c1",
        label: "1",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.0625,
        maxAmp: 8,
      },
      {
        id: "c2",
        label: "2",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.1875,
        maxAmp: 8,
      },
      {
        id: "c3",
        label: "3",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.3125,
        maxAmp: 8,
      },
      {
        id: "c4",
        label: "4",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.4375,
        maxAmp: 8,
      },
      {
        id: "c5",
        label: "5",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.5625,
        maxAmp: 8,
      },
      {
        id: "c6",
        label: "6",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.6875,
        maxAmp: 8,
      },
      {
        id: "c7",
        label: "7",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.8125,
        maxAmp: 8,
      },
      {
        id: "c8",
        label: "8",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.9375,
        maxAmp: 8,
      },
    ],
    image: "dist-philippi-stv108.webp",
  },
  {
    id: "dist-battery-isolator-200",
    // Victron Argo FET/diode documentation: charge banks without connecting them.
    // https://www.victronenergy.com/battery-isolators-and-combiners/argo-fet-battery-isolators
    oneWayDcConnections: [["alt-in", "batt1-out"], ["alt-in", "batt2-out"]],
    name: "Diode Battery Isolator 200A",
    shortName: "Diode Iso 200A",
    subtitle: "Dual-battery diode isolator",
    category: "distribution",
    width: 200,
    height: 110,
    terminals: [
      {
        id: "alt-in",
        label: "ALT",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 200,
      },
      {
        id: "batt1-out",
        label: "BATT 1",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 100,
      },
      {
        id: "batt2-out",
        label: "BATT 2",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 100,
      },
    ],
  },
  {
    id: "dist-victron-argofet-100-2",
    // Victron Argo FET/diode documentation: charge banks without connecting them.
    // https://www.victronenergy.com/battery-isolators-and-combiners/argo-fet-battery-isolators
    oneWayDcConnections: [["alt-in", "out1"], ["alt-in", "out2"]],
    name: "Victron Argofet 100-2",
    shortName: "Argofet 100-2",
    subtitle: "Alternator splitter 2-out 100A",
    category: "distribution",
    manufacturer: "Victron",
    model: "Argofet 100-2",
    width: 200,
    height: 120,
    terminals: [
      {
        id: "alt-in",
        label: "ALT",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 100,
      },
      {
        id: "out1",
        label: "OUT 1",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 100,
      },
      {
        id: "out2",
        label: "OUT 2",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 100,
      },
    ],
    image: "dist-victron-argofet-100-2.webp",
  },
  {
    id: "dist-victron-argofet-100-3",
    // Victron Argo FET/diode documentation: charge banks without connecting them.
    // https://www.victronenergy.com/battery-isolators-and-combiners/argo-fet-battery-isolators
    oneWayDcConnections: [["alt-in", "out1"], ["alt-in", "out2"], ["alt-in", "out3"]],
    name: "Victron Argofet 100-3",
    shortName: "Argofet 100-3",
    subtitle: "Alternator splitter 3-out 100A",
    category: "distribution",
    manufacturer: "Victron",
    model: "Argofet 100-3",
    width: 210,
    height: 130,
    terminals: [
      {
        id: "alt-in",
        label: "ALT",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 100,
      },
      {
        id: "out1",
        label: "OUT 1",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.2,
        maxAmp: 100,
      },
      {
        id: "out2",
        label: "OUT 2",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 100,
      },
      {
        id: "out3",
        label: "OUT 3",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.8,
        maxAmp: 100,
      },
    ],
    image: "dist-victron-argofet-100-3.webp",
  },
  {
    id: "dist-victron-cyrix-ct-120",
    name: "Victron Cyrix-ct 12/24V 120A",
    shortName: "Cyrix-ct 120",
    subtitle: "Combiner relay 120A",
    category: "distribution",
    manufacturer: "Victron",
    model: "Cyrix-ct 12/24-120",
    width: 190,
    height: 105,
    terminals: [
      {
        id: "a",
        label: "A",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 120,
      },
      {
        id: "b",
        label: "B",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 120,
      },
    ],
    // Manual contact scenarios; automatic voltage/control behavior is not
    // inferred from nominal battery voltage. Static design retains both banks.
    simulateStates: {
      default: "isolated",
      options: [
        { id: "combined", label: "Combined (manual)", intraLinks: [["a", "b"]] },
        { id: "isolated", label: "Isolated (manual)", ampOverride: 0 },
      ],
    },
    image: "dist-victron-cyrix-ct-120.webp",
  },
  {
    id: "dist-victron-cyrix-ct-230",
    name: "Victron Cyrix-ct 12/24V 230A",
    shortName: "Cyrix-ct 230",
    subtitle: "Combiner relay 230A",
    category: "distribution",
    manufacturer: "Victron",
    model: "Cyrix-ct 12/24-230",
    width: 200,
    height: 110,
    terminals: [
      {
        id: "a",
        label: "A",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 230,
      },
      {
        id: "b",
        label: "B",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 230,
      },
    ],
    // Manual contact scenarios; automatic voltage/control behavior is not
    // inferred from nominal battery voltage. Static design retains both banks.
    simulateStates: {
      default: "isolated",
      options: [
        { id: "combined", label: "Combined (manual)", intraLinks: [["a", "b"]] },
        { id: "isolated", label: "Isolated (manual)", ampOverride: 0 },
      ],
    },
    image: "dist-victron-cyrix-ct-230.webp",
  },
  {
    id: "dist-victron-lynx-smart-bms-500",
    currentShunt: { batteryTerminalId: "batt-neg", systemTerminalId: "bus-neg", referenceTerminalId: "batt-pos" },
    name: "Victron Lynx Smart BMS 500",
    shortName: "Lynx Smart 500",
    subtitle: "Lynx-bus BMS + 500A shunt",
    category: "distribution",
    manufacturer: "Victron",
    model: "Lynx Smart BMS 500",
    width: 230,
    height: 145,
    terminals: [
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 500,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 500,
      },
      {
        id: "bus-pos",
        label: "BUS +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 500,
      },
      {
        id: "bus-neg",
        label: "BUS −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 500,
      },
    ],
    // Victron publishes ~0.6 W standby / ~2.6 W on at 12 V; the ON figure is
    // the one a permanently-live system pays.
    ampDraw: 0.217,
    bms: {
      placement: "external",
      switching: { mode: "contactor" },
      // The contactor and shunt are bidirectional and Victron publishes one
      // continuous figure, not separate charge and discharge numbers.
      continuousDischargeA: 500,
      continuousChargeA: 500,
      // 600 A for FIVE MINUTES — a thermal contactor rating. Never a cranking
      // number: routed into SURGE_HOLD_FACTOR it would corrupt starter fusing.
      surge: [{ amps: 600, seconds: 300 }],
      controls: [
        { role: "charge-permit", kind: "dry-contact", maxA: 0.5, maxV: 60 },
        { role: "discharge-permit", kind: "dry-contact", maxA: 0.5, maxV: 60 },
        { role: "pre-alarm", kind: "dry-contact", maxA: 2, maxV: 60 },
        // The programmable SPDT relay in "Alternator ATC" mode, opening 2 s
        // before the contactor so an external regulator can shed field first.
        { role: "alternator-field", kind: "dry-contact", maxA: 2, maxV: 60 },
      ],
      requiresExternalSwitchgear: false,
      busSeparation: "dual",
      alternatorControl: ["charge-relay", "field-cut"],
      // Lithium Battery Smart only. The Lynx Smart BMS NG carries an identical
      // 500 A rating for the incompatible NG range — never match on amps alone.
      compatibleBatteryIds: [
        "batt-victron-lfp-100",
        "batt-victron-lfp-200",
        "batt-victron-lfp-330",
      ],
      // Victron's BMS datasheets publish no temperature threshold; it lives on
      // the battery ("Allowed-To-Charge minimum temperature", default 5 °C).
      unpublished: ["lowTempChargeCutoffC"],
    },
    image: "dist-victron-lynx-smart-bms-500.webp",
  },
  {
    id: "dist-victron-lynx-smart-bms-1000",
    currentShunt: { batteryTerminalId: "batt-neg", systemTerminalId: "bus-neg", referenceTerminalId: "batt-pos" },
    name: "Victron Lynx Smart BMS 1000",
    shortName: "Lynx Smart 1000",
    subtitle: "Lynx-bus BMS + 1000A shunt",
    category: "distribution",
    manufacturer: "Victron",
    model: "Lynx Smart BMS 1000",
    width: 240,
    height: 150,
    terminals: [
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 1000,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 1000,
      },
      {
        id: "bus-pos",
        label: "BUS +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 1000,
      },
      {
        id: "bus-neg",
        label: "BUS −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 1000,
      },
    ],
    // ~0.6 W standby / ~4.2 W on at 12 V.
    ampDraw: 0.35,
    bms: {
      placement: "external",
      switching: { mode: "contactor" },
      continuousDischargeA: 1000,
      continuousChargeA: 1000,
      // 1200 A for FIVE MINUTES — thermal, not a cranking rating. See the 500.
      surge: [{ amps: 1200, seconds: 300 }],
      controls: [
        { role: "charge-permit", kind: "dry-contact", maxA: 0.5, maxV: 60 },
        { role: "discharge-permit", kind: "dry-contact", maxA: 0.5, maxV: 60 },
        { role: "pre-alarm", kind: "dry-contact", maxA: 2, maxV: 60 },
        { role: "alternator-field", kind: "dry-contact", maxA: 2, maxV: 60 },
      ],
      requiresExternalSwitchgear: false,
      busSeparation: "dual",
      alternatorControl: ["charge-relay", "field-cut"],
      compatibleBatteryIds: [
        "batt-victron-lfp-100",
        "batt-victron-lfp-200",
        "batt-victron-lfp-330",
      ],
      unpublished: ["lowTempChargeCutoffC"],
    },
    image: "dist-victron-lynx-smart-bms-1000.webp",
  },
  {
    id: "bms-victron-vebus-bms-v2",
    name: "Victron VE.Bus BMS V2",
    shortName: "VE.Bus BMS",
    subtitle: "BMS for Smart Lithium",
    category: "distribution",
    manufacturer: "Victron",
    model: "VE.Bus BMS V2",
    width: 160,
    height: 90,
    terminals: [
      {
        id: "pwr-pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 1,
      },
    ],
    ampDraw: 0.01,
    bms: {
      placement: "external",
      switching: { mode: "signal" },
      controls: [
        { role: "discharge-permit", kind: "source", maxA: 1 },
        // 10 mA — two orders below the smallBMS NG's, and nowhere near a relay
        // coil. It drives a Cyrix-Li-ct control pin and nothing heavier.
        { role: "charge-permit", kind: "source", maxA: 0.01 },
        { role: "pre-alarm", kind: "source", maxA: 1 },
      ],
      // The MultiPlus/Quattro is disabled over VE.Bus with no extra hardware,
      // but DC loads still need a BatteryProtect and chargers a Cyrix.
      requiresExternalSwitchgear: true,
      busSeparation: "dual",
      alternatorControl: ["charge-relay"],
      compatibleBatteryIds: [
        "batt-victron-lfp-100",
        "batt-victron-lfp-200",
        "batt-victron-lfp-330",
      ],
      unpublished: ["lowTempChargeCutoffC"],
    },
    image: "bms-victron-vebus-bms-v2.webp",
  },
  {
    id: "bms-victron-smallbms-ng",
    name: "Victron smallBMS NG",
    shortName: "smallBMS NG",
    subtitle: "Compact BMS for Lithium NG",
    category: "distribution",
    manufacturer: "Victron",
    model: "smallBMS NG",
    width: 150,
    height: 85,
    terminals: [
      {
        id: "pwr-pos",
        label: "+",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 1,
      },
      {
        id: "pwr-neg",
        label: "−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 1,
      },
    ],
    ampDraw: 0.003,
    bms: {
      placement: "external",
      switching: { mode: "signal" },
      controls: [
        { role: "discharge-permit", kind: "source", maxA: 1 },
        { role: "charge-permit", kind: "source", maxA: 0.5 },
        // Victron contradicts itself here: 1 A in the datasheet's features
        // prose, 500 mA in its spec table and in the HTML manual. Table wins.
        { role: "pre-alarm", kind: "source", maxA: 0.5 },
      ],
      requiresExternalSwitchgear: true,
      busSeparation: "dual",
      alternatorControl: ["charge-relay"],
      compatibleBatteryIds: ["batt-victron-lfp-ng-200"],
      unpublished: ["lowTempChargeCutoffC"],
    },
    image: "bms-victron-smallbms-ng.webp",
  },
  {
    id: "bms-deligreen-8s-24v",
    name: "Deligreen 8S 24V LiFePO4 BMS",
    shortName: "Deligreen 8S BMS",
    subtitle: "24V LiFePO4 BMS (common-port)",
    category: "distribution",
    compatibleVoltages: [24],
    manufacturer: "Deligreen",
    model: "8S 24V smart BMS",
    // Exact Deligreen board identity remains unresolved (manual review 2026-10-01).
    // Keep the intended common-port negative topology; the stock photo and a
    // different Daly model do not verify this entry's chemistry or ratings.
    width: 190,
    height: 110,
    terminals: [
      {
        id: "b-neg",
        label: "B−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 150,
      },
      {
        id: "p-neg",
        label: "P−",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 150,
      },
    ],
    bms: {
      placement: "external",
      switching: { mode: "fet", leg: "negative" },
      continuousDischargeA: 150,
      // Common port: one switched node, so an overvoltage disconnect leaves the
      // charger connected to the loads.
      busSeparation: "single",
      chemistrySupport: [{ chemistry: "lifepo4", cellsMin: 8, cellsMax: 8 }],
      requiresExternalSwitchgear: false,
      // Deligreen publishes nothing for this SKU. The charge current printed on
      // the product photo is not usable — that photo is a family stock shot
      // labelled "Li-ion", not this LiFePO4 board — and Daly's own document for
      // the 100 A sibling rates charge at half of discharge.
      unpublished: [
        "continuousChargeA",
        "surge",
        "controls",
        "lowTempChargeCutoffC",
      ],
    },
    image: "bms-deligreen-8s-24v.webp",
  },
  {
    id: "bms-jk-b2a8s30p",
    name: "JK BMS JK-B2A8S30P 300 A",
    shortName: "B2A8S30P 300A",
    subtitle: "12/24V BMS · 2A active balancing",
    category: "distribution",
    compatibleVoltages: [12, 24],
    manufacturer: "JK",
    model: "JK-B2A8S30P",
    // Jikong JK-B2A8S-30P V19.0.1, pp. 7–9, 16, 18, 24:
    // https://raw.githubusercontent.com/JIKBMS/files/4328652ab2507844801a8338a7bab754eaffe1fd/JK-B2A8S30P%20V19.0.1.pdf
    // Main negative path only; cell taps, B+ supply and sensors are in the harness.
    width: 200,
    height: 120,
    terminals: [
      {
        id: "b-neg",
        label: "B−",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 300,
      },
      {
        id: "p-neg",
        label: "P−",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 300,
      },
    ],
    bms: {
      placement: "external",
      switching: { mode: "fet", leg: "negative" },
      continuousDischargeA: 300,
      continuousChargeA: 300,
      // No duration is published for the instantaneous rating; it is not inrush.
      surge: [{ amps: 600, seconds: null }],
      busSeparation: "single",
      requiresExternalSwitchgear: false,
      chemistrySupport: [
        { chemistry: "lifepo4", cellsMin: 3, cellsMax: 8 },
        { chemistry: "li-ion", cellsMin: 3, cellsMax: 8 },
        { chemistry: "lto", cellsMin: 7, cellsMax: 8 },
      ],
      // Factory preset, not a cell manufacturer's safe charging threshold.
      lowTempChargeCutoffC: -20,
      lowTempCutoffConfigurable: true,
    },
  },
  // Manufacturer evidence and model boundaries: docs/research/component-manuals-common-50-2026-10-01.md.
  {
    "id": "dist-ctek-smartpass-120s",
    "image": "dist-ctek-smartpass-120s.webp",
    "name": "CTEK SMARTPASS 120S",
    "shortName": "SMARTPASS 120S",
    "subtitle": "Battery / consumer management",
    "category": "distribution",
    "manufacturer": "CTEK",
    "model": "SMARTPASS 120S (1058)",
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "width": 220,
    "height": 115,
    "terminals": [
      {
        "id": "alt-pos",
        "label": "ALTERNATOR +",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 120,
        "side": "left",
        "offset": 0.5
      },
      {
        "id": "service-pos",
        "label": "SERVICE +",
        "polarity": "positive",
        "direction": "bidi",
        "maxAmp": 120,
        "side": "right",
        "offset": 0.25
      },
      {
        "id": "consumer-pos",
        "label": "CONSUMERS +",
        "polarity": "positive",
        "direction": "output",
        "maxAmp": 80,
        "side": "right",
        "offset": 0.75
      }
    ],
    "simulateStates": {
      "default": "service",
      "options": [
        {
          "id": "service",
          "label": "Service battery supplies consumers",
          "intraLinks": [
            [
              "service-pos",
              "consumer-pos"
            ]
          ]
        },
        {
          "id": "charging",
          "label": "Alternator charging",
          "intraLinks": [
            [
              "alt-pos",
              "service-pos"
            ],
            [
              "alt-pos",
              "consumer-pos"
            ]
          ]
        },
        {
          "id": "guard",
          "label": "Consumer battery guard",
          "intraLinks": []
        }
      ]
    }
  },
  {
    "id": "dist-sterling-apd12",
    "image": "dist-sterling-apd12.webp",
    "name": "Sterling APD12 Alternator Protection Device",
    "shortName": "APD12",
    "subtitle": "Alternator transient protection",
    "category": "distribution",
    "manufacturer": "Sterling",
    "model": "APD12",
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "width": 215,
    "height": 85,
    "terminals": []
  },
  {
    "id": "fuse-block-blue-sea-7748",
    "equipmentLimitExemption": "fuse",
    "name": "Blue Sea SafetyHub 150 Fuse Block",
    "shortName": "SafetyHub 150",
    "subtitle": "4 MIDI + 6 ATC circuits",
    "category": "distribution",
    "manufacturer": "Blue Sea Systems",
    "model": "7748",
    "compatibleVoltages": [
      12,
      24
    ],
    "isFuseBlock": true,
    "width": 250,
    "height": 300,
    "terminals": [
      {
        "id": "in",
        "label": "BATT +",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 200,
        "side": "left",
        "offset": 0.2
      },
      {
        "id": "neg-in",
        "label": "NEG BUS",
        "polarity": "negative",
        "direction": "bidi",
        "maxAmp": 200,
        "side": "left",
        "offset": 0.75
      },
      {
        "id": "midi-1",
        "label": "MIDI 1",
        "polarity": "positive",
        "direction": "output",
        "maxAmp": 200,
        "side": "right",
        "offset": 0.06
      },
      {
        "id": "midi-2",
        "label": "MIDI 2",
        "polarity": "positive",
        "direction": "output",
        "maxAmp": 200,
        "side": "right",
        "offset": 0.15
      },
      {
        "id": "midi-3",
        "label": "MIDI 3",
        "polarity": "positive",
        "direction": "output",
        "maxAmp": 200,
        "side": "right",
        "offset": 0.24
      },
      {
        "id": "midi-4",
        "label": "MIDI 4",
        "polarity": "positive",
        "direction": "output",
        "maxAmp": 200,
        "side": "right",
        "offset": 0.33
      },
      {
        "id": "atc-1",
        "label": "ATC 1",
        "polarity": "positive",
        "direction": "output",
        "maxAmp": 30,
        "side": "right",
        "offset": 0.43
      },
      {
        "id": "atc-2",
        "label": "ATC 2",
        "polarity": "positive",
        "direction": "output",
        "maxAmp": 30,
        "side": "right",
        "offset": 0.52
      },
      {
        "id": "atc-3",
        "label": "ATC 3",
        "polarity": "positive",
        "direction": "output",
        "maxAmp": 30,
        "side": "right",
        "offset": 0.61
      },
      {
        "id": "atc-4",
        "label": "ATC 4",
        "polarity": "positive",
        "direction": "output",
        "maxAmp": 30,
        "side": "right",
        "offset": 0.7
      },
      {
        "id": "atc-5",
        "label": "ATC 5",
        "polarity": "positive",
        "direction": "output",
        "maxAmp": 30,
        "side": "right",
        "offset": 0.79
      },
      {
        "id": "atc-6",
        "label": "ATC 6",
        "polarity": "positive",
        "direction": "output",
        "maxAmp": 30,
        "side": "right",
        "offset": 0.8799999999999999
      },
      {
        "id": "neg-load",
        "label": "RETURNS −",
        "polarity": "negative",
        "direction": "bidi",
        "maxAmp": 200,
        "side": "left",
        "offset": 0.92
      }
    ]
  },
  {
    "id": "breaker-blue-sea-7184",
    "name": "Blue Sea 285-Series 60A Breaker",
    "shortName": "285-Series 60A",
    "subtitle": "Manual-reset DC breaker",
    "category": "distribution",
    "manufacturer": "Blue Sea Systems",
    "model": "7184",
    "compatibleVoltages": [
      12,
      24,
      48
    ],
    "renderVariant": "switch",
    "width": 150,
    "height": 80,
    "terminals": [
      {
        "id": "in",
        "label": "LINE",
        "polarity": "positive",
        "direction": "bidi",
        "maxAmp": 60,
        "side": "left",
        "offset": 0.5
      },
      {
        "id": "out",
        "label": "LOAD",
        "polarity": "positive",
        "direction": "bidi",
        "maxAmp": 60,
        "side": "right",
        "offset": 0.5
      }
    ]
  },
  {
    "id": "shore-elci-blue-sea-8100",
    "name": "Blue Sea 8100 AC ELCI Main Circuit Breaker Panel 30A 120V",
    "shortName": "ELCI breaker 30A 120V",
    "subtitle": "AC circuit breakers · shore main panel",
    "category": "distribution",
    "manufacturer": "Blue Sea Systems",
    "model": "8100",
    "voltage": 120,
    "width": 225,
    "height": 140,
    "terminals": [
      {
        "id": "ac-in-l",
        "label": "LINE L",
        "polarity": "neutral",
        "direction": "input",
        "maxAmp": 30,
        "side": "left",
        "offset": 0.2
      },
      {
        "id": "ac-in-n",
        "label": "LINE N",
        "polarity": "neutral",
        "direction": "input",
        "maxAmp": 30,
        "side": "left",
        "offset": 0.5
      },
      {
        "id": "ac-in-pe",
        "label": "EARTH IN",
        "polarity": "ground",
        "direction": "bidi",
        "maxAmp": 30,
        "side": "left",
        "offset": 0.8
      },
      {
        "id": "ac-out-l",
        "label": "LOAD L",
        "polarity": "neutral",
        "direction": "output",
        "maxAmp": 30,
        "side": "right",
        "offset": 0.2
      },
      {
        "id": "ac-out-n",
        "label": "LOAD N",
        "polarity": "neutral",
        "direction": "output",
        "maxAmp": 30,
        "side": "right",
        "offset": 0.5
      },
      {
        "id": "ac-out-pe",
        "label": "EARTH OUT",
        "polarity": "ground",
        "direction": "bidi",
        "maxAmp": 30,
        "side": "right",
        "offset": 0.8
      }
    ],
    "simulateStates": {
      "default": "on",
      "options": [
        {
          "id": "on",
          "label": "On",
          "intraLinks": [
            [
              "ac-in-l",
              "ac-out-l"
            ],
            [
              "ac-in-n",
              "ac-out-n"
            ],
            [
              "ac-in-pe",
              "ac-out-pe"
            ]
          ]
        },
        {
          "id": "tripped",
          "label": "Off / tripped",
          "intraLinks": [
            [
              "ac-in-pe",
              "ac-out-pe"
            ]
          ]
        }
      ]
    }
  },
  {
    "id": "shore-victron-isolation-transformer-3600",
    "name": "Victron Isolation Transformer 3600W 115/230V",
    "shortName": "Isolation 3600W",
    "subtitle": "Marine shore isolation",
    "category": "distribution",
    "manufacturer": "Victron",
    "model": "ITR040362042",
    "voltage": 230,
    "width": 250,
    "height": 155,
    "terminals": [
      {
        "id": "ac-in-l",
        "label": "SHORE L",
        "polarity": "neutral",
        "direction": "input",
        "maxAmp": 16,
        "side": "left",
        "offset": 0.2
      },
      {
        "id": "ac-in-n",
        "label": "SHORE N",
        "polarity": "neutral",
        "direction": "input",
        "maxAmp": 16,
        "side": "left",
        "offset": 0.5
      },
      {
        "id": "ac-in-pe",
        "label": "SHORE PE",
        "polarity": "ground",
        "direction": "input",
        "maxAmp": 16,
        "side": "left",
        "offset": 0.8
      },
      {
        "id": "ac-out-l",
        "label": "VESSEL L",
        "polarity": "neutral",
        "direction": "output",
        "maxAmp": 16,
        "side": "right",
        "offset": 0.2
      },
      {
        "id": "ac-out-n",
        "label": "VESSEL N",
        "polarity": "neutral",
        "direction": "output",
        "maxAmp": 16,
        "side": "right",
        "offset": 0.5
      },
      {
        "id": "ac-out-pe",
        "label": "VESSEL PE",
        "polarity": "ground",
        "direction": "output",
        "maxAmp": 16,
        "side": "right",
        "offset": 0.8
      }
    ],
    "simulateStates": {
      "default": "afloat",
      "options": [
        {
          "id": "afloat",
          "label": "Afloat / isolated",
          "intraLinks": [
            [
              "ac-out-n",
              "ac-out-pe"
            ]
          ]
        }
      ]
    }
  },
  // Additional manufacturer-reviewed models; evidence: docs/research/component-manuals-additional-50-2026-10-01.md
  {
    "id": "bms-victron-smart-12-200",
    "image": "bms-victron-smart-12-200.webp",
    "name": "Victron Smart BMS 12/200",
    "shortName": "Smart BMS 12/200",
    "subtitle": "12 V Lithium Battery Smart BMS with a 200 A system port and separately limited alternator input",
    "category": "distribution",
    "manufacturer": "Victron",
    "model": "Smart BMS 12/200",
    "terminals": [
      {
        "id": "batt-pos",
        "label": "BATTERY +",
        "polarity": "positive",
        "direction": "bidi",
        "maxAmp": 200,
        "side": "left",
        "offset": 0.25
      },
      {
        "id": "system-pos",
        "label": "SYSTEM +",
        "polarity": "positive",
        "direction": "bidi",
        "maxAmp": 200,
        "side": "right",
        "offset": 0.25
      },
      {
        "id": "alt-pos",
        "label": "ALTERNATOR +",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 100,
        "side": "left",
        "offset": 0.7
      },
      {
        "id": "gnd",
        "label": "GND",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 0,
        "side": "right",
        "offset": 0.7
      },
      {
        "id": "load-disconnect",
        "label": "LOAD DISCONNECT",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0.01,
        "side": "bottom",
        "offset": 0.1
      },
      {
        "id": "charge-disconnect",
        "label": "CHARGE DISCONNECT",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0.01,
        "side": "bottom",
        "offset": 0.3
      },
      {
        "id": "pre-alarm",
        "label": "PRE-ALARM",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 1,
        "side": "bottom",
        "offset": 0.5
      },
      {
        "id": "remote-l",
        "label": "REMOTE L",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.7
      },
      {
        "id": "remote-h",
        "label": "REMOTE H",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.9
      },
      {
        "id": "bms-chain",
        "label": "BATTERY BMS CHAIN",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "top",
        "offset": 0.5
      }
    ],
    "width": 180,
    "height": 130,
    "compatibleVoltages": [
      12
    ],
    "simulateStates": {
      "default": "on",
      "options": [
        {
          "id": "on",
          "label": "On",
          "intraLinks": [
            [
              "batt-pos",
              "system-pos"
            ]
          ]
        },
        {
          "id": "off",
          "label": "Off",
          "ampOverride": 0,
          "intraLinks": []
        }
      ]
    },
    "bms": {
      "placement": "external",
      "switching": {
        "mode": "fet",
        "leg": "positive"
      },
      "continuousDischargeA": 200,
      "continuousChargeA": 200,
      "surge": [
        {
          "amps": 400,
          "seconds": null
        }
      ],
      "busSeparation": "single",
      "controls": [
        {
          "role": "charge-permit",
          "kind": "source",
          "maxA": 0.01
        },
        {
          "role": "discharge-permit",
          "kind": "source",
          "maxA": 0.01
        },
        {
          "role": "pre-alarm",
          "kind": "source",
          "maxA": 1
        }
      ],
      "compatibleBatteryIds": [
        "batt-victron-lfp-100",
        "batt-victron-lfp-200",
        "batt-victron-lfp-330"
      ],
      "unpublished": [
        "lowTempChargeCutoffC"
      ]
    }
  },
  {
    "id": "dist-bep-701-md",
    "image": "dist-bep-701-md.webp",
    "name": "BEP 701-MD Motorised Battery Switch",
    "shortName": "701-MD",
    "subtitle": "275 A continuous remote battery switch with manual override",
    "category": "distribution",
    "manufacturer": "BEP",
    "model": "701-MD",
    "terminals": [
      {
        "id": "in-pos",
        "label": "BATTERY INPUT",
        "polarity": "positive",
        "direction": "bidi",
        "maxAmp": 275,
        "side": "left",
        "offset": 0.3
      },
      {
        "id": "out-pos",
        "label": "LOAD OUTPUT",
        "polarity": "positive",
        "direction": "bidi",
        "maxAmp": 275,
        "side": "right",
        "offset": 0.3
      },
      {
        "id": "led",
        "label": "PIN 1 RED LED",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.2
      },
      {
        "id": "remote",
        "label": "PIN 2 GREEN COMMAND",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.5
      },
      {
        "id": "control-neg",
        "label": "PIN 3 BLACK −",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.8
      }
    ],
    "width": 180,
    "height": 130,
    "renderVariant": "switch",
    "compatibleVoltages": [
      12,
      24
    ],
    "simulateStates": {
      "default": "on",
      "options": [
        {
          "id": "on",
          "label": "On",
          "intraLinks": [
            [
              "in-pos",
              "out-pos"
            ]
          ]
        },
        {
          "id": "off",
          "label": "Off",
          "ampOverride": 0,
          "intraLinks": []
        }
      ]
    }
  },
  {
    "id": "dist-rule-35a",
    "image": "dist-rule-35a.webp",
    "name": "Rule Rule-A-Matic 35A Float Switch",
    "shortName": "Rule-A-Matic 35A",
    "subtitle": "Two-wire bilge float switch",
    "category": "distribution",
    "manufacturer": "Rule",
    "model": "35A",
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "width": 250,
    "height": 140,
    "terminals": [
      {
        "id": "in",
        "label": "Supply +",
        "polarity": "positive",
        "direction": "input",
        "side": "left",
        "offset": 0.5,
        "maxAmp": 14
      },
      {
        "id": "out",
        "label": "Pump +",
        "polarity": "positive",
        "direction": "output",
        "side": "right",
        "offset": 0.5,
        "maxAmp": 14
      }
    ],
    "simulateStates": {
      "default": "wet",
      "options": [
        {
          "id": "wet",
          "label": "Water high / conducting",
          "intraLinks": [
            [
              "in",
              "out"
            ]
          ]
        },
        {
          "id": "dry",
          "label": "Water low / open",
          "intraLinks": []
        }
      ]
    }
  },
  {
    "id": "dist-johnson-ultima-switch",
    "image": "dist-johnson-ultima-switch.webp",
    "name": "Johnson Pump Ultima Switch 36303",
    "shortName": "Ultima Switch",
    "subtitle": "Electronic bilge level switch",
    "category": "distribution",
    "manufacturer": "Johnson Pump",
    "model": "36303 / 34-36303",
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "width": 250,
    "height": 140,
    "terminals": [
      {
        "id": "in",
        "label": "Brown/white +",
        "polarity": "positive",
        "direction": "input",
        "side": "left",
        "offset": 0.5,
        "maxAmp": 20
      },
      {
        "id": "out",
        "label": "Brown to pump",
        "polarity": "positive",
        "direction": "output",
        "side": "right",
        "offset": 0.5,
        "maxAmp": 20
      }
    ],
    "simulateStates": {
      "default": "wet",
      "options": [
        {
          "id": "wet",
          "label": "Water high / conducting",
          "intraLinks": [
            [
              "in",
              "out"
            ]
          ]
        },
        {
          "id": "dry",
          "label": "Water low / open",
          "intraLinks": []
        }
      ]
    }
  },
];
